import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Phone, Mail, MessageCircle, Plus, Brain } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [lead, setLead] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupData, setFollowupData] = useState({
    type: "phone",
    notes: "",
    outcome: "interested",
    next_followup_date: "",
  });

  useEffect(() => {
    fetchLeadData();
  }, [id]);

  const fetchLeadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [leadRes, followupsRes] = await Promise.all([
        axios.get(`${API}/leads/${id}`, { headers }),
        axios.get(`${API}/followups?lead_id=${id}`, { headers }),
      ]);

      setLead(leadRes.data);
      setFollowups(followupsRes.data);
    } catch (error) {
      toast.error("Failed to load lead details");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFollowupSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const data = {
        lead_id: id,
        type: followupData.type,
        notes: followupData.notes,
        outcome: followupData.outcome,
        next_followup_date: followupData.next_followup_date || null,
      };

      await axios.post(`${API}/followups`, data, { headers });
      toast.success("Follow-up added successfully");
      setShowFollowupForm(false);
      setFollowupData({
        type: "phone",
        notes: "",
        outcome: "interested",
        next_followup_date: "",
      });
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to add follow-up");
    }
  };

  const calculateScore = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/leads/${id}/score`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`AI Score updated: ${response.data.ai_score.toFixed(0)}`);
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to calculate AI score");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  const getFollowupIcon = (type) => {
    switch (type) {
      case "phone":
        return <Phone className="h-5 w-5" />;
      case "email":
        return <Mail className="h-5 w-5" />;
      case "whatsapp":
      case "sms":
        return <MessageCircle className="h-5 w-5" />;
      default:
        return <MessageCircle className="h-5 w-5" />;
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-in">
        <Button
          data-testid="back-button"
          onClick={() => navigate("/leads")}
          variant="ghost"
          className="mb-6 -ml-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leads
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Lead Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Lead Details
                </h2>
                <Button
                  data-testid="edit-lead-button"
                  onClick={() => navigate(`/leads/edit/${id}`)}
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                >
                  Edit
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Name</p>
                  <p className="text-lg font-semibold text-slate-900">{lead.name}</p>
                </div>

                {user.role !== 'sourcing_manager' ? (
                  <>
                    <div>
                      <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Email</p>
                      <p className="text-slate-900">{lead.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Phone</p>
                      <p className="text-slate-900">{lead.phone}</p>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">Contact details restricted. Please coordinate with CAPIN Realty admin for full contact information.</p>
                  </div>
                )}

                {lead.property_title && (
                  <div>
                    <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Property Interest</p>
                    <p className="text-amber-600 font-medium">{lead.property_title}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Source</p>
                  <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded uppercase font-medium">
                    {lead.source}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Status</p>
                  <span className={`status-badge status-${lead.status}`}>{lead.status}</span>
                </div>

                {lead.notes && (
                  <div>
                    <p className="text-sm text-slate-600 uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-slate-900 text-sm">{lead.notes}</p>
                  </div>
                )}
              </div>

              {/* AI Score */}
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-md border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-900 uppercase tracking-wide">AI Lead Score</p>
                  <button
                    data-testid="recalculate-score-button"
                    onClick={calculateScore}
                    className="text-blue-900 hover:text-blue-700 transition-colors"
                    title="Recalculate Score"
                  >
                    <Brain className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-4xl font-bold text-blue-900">{lead.ai_score?.toFixed(0) || "--"}</p>
                <p className="text-xs text-blue-700 mt-1">Based on engagement & activity</p>
              </div>
            </div>
          </div>

          {/* Right Column - Follow-ups (Staff Only) */}
          {['admin', 'team_leader', 'agent', 'sourcing_manager'].includes(user.role) && (
          <div className="lg:col-span-2">
            <div className="bg-white rounded-md border border-slate-200 shadow-sm">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Follow-up History
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{followups.length}/5 follow-ups used</p>
                  </div>
                  {followups.length < 5 ? (
                  <Button
                    data-testid="add-followup-button"
                    onClick={() => setShowFollowupForm(!showFollowupForm)}
                    className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Follow-up
                  </Button>
                  ) : (
                  <span className="text-sm text-amber-600 font-medium bg-amber-50 px-3 py-1.5 rounded border border-amber-200">Max 5 follow-ups reached</span>
                  )}
                </div>
              </div>

              <div className="p-6">
                {/* Follow-up Form */}
                {showFollowupForm && (
                  <form onSubmit={handleFollowupSubmit} className="mb-6 p-6 bg-slate-50 rounded-md border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">New Follow-up</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="type">Communication Type *</Label>
                          <select
                            id="type"
                            name="type"
                            data-testid="followup-type-select"
                            value={followupData.type}
                            onChange={handleFollowupChange}
                            className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                            required
                          >
                            <option value="phone">Phone Call</option>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="sms">SMS</option>
                            <option value="email">Email</option>
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="outcome">Outcome *</Label>
                          <select
                            id="outcome"
                            name="outcome"
                            data-testid="followup-outcome-select"
                            value={followupData.outcome}
                            onChange={handleFollowupChange}
                            className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                            required
                          >
                            <option value="interested">Interested</option>
                            <option value="not_interested">Not Interested</option>
                            <option value="callback">Callback Requested</option>
                            <option value="meeting_scheduled">Meeting Scheduled</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="notes">Notes *</Label>
                        <Textarea
                          id="notes"
                          name="notes"
                          data-testid="followup-notes-input"
                          value={followupData.notes}
                          onChange={handleFollowupChange}
                          className="mt-1 rounded-sm"
                          required
                          placeholder="What was discussed? Any action items?"
                        />
                      </div>

                      <div>
                        <Label htmlFor="next_followup_date">Next Follow-up Date (Optional)</Label>
                        <Input
                          id="next_followup_date"
                          name="next_followup_date"
                          type="datetime-local"
                          data-testid="followup-date-input"
                          value={followupData.next_followup_date}
                          onChange={handleFollowupChange}
                          className="mt-1 h-10 rounded-sm"
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowFollowupForm(false)}
                          className="rounded-sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          data-testid="submit-followup-button"
                          type="submit"
                          className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
                        >
                          Add Follow-up
                        </Button>
                      </div>
                    </div>
                  </form>
                )}

                {/* Follow-ups List */}
                {followups.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No follow-ups recorded yet</p>
                ) : (
                  <div className="space-y-4">
                    {followups.map((followup) => (
                      <div
                        key={followup.id}
                        data-testid={`followup-item-${followup.id}`}
                        className="p-4 bg-slate-50 rounded-sm border border-slate-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-900/10 flex items-center justify-center text-blue-900">
                              {getFollowupIcon(followup.type)}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900 capitalize">{followup.type} Call</p>
                              <p className="text-xs text-slate-600">
                                {format(new Date(followup.created_at), "PPp")}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded uppercase font-medium ${
                              followup.outcome === "interested" || followup.outcome === "meeting_scheduled"
                                ? "bg-green-100 text-green-800"
                                : followup.outcome === "not_interested"
                                ? "bg-red-100 text-red-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {followup.outcome.replace("_", " ")}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{followup.notes}</p>
                        {followup.next_followup_date && (
                          <p className="text-xs text-amber-600 mt-2">
                            Next follow-up: {format(new Date(followup.next_followup_date), "PPp")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
